/* eslint-disable max-statements, complexity, max-lines, indent */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { arrayOf, func, number, object, string } from 'prop-types'
import Payment from 'payment'
import Modal from '../Modal/Modal'
import IcoCard from '../icons/IcoCard'
import IcoGrip from '../icons/IcoGrip'
import IcoUp from '../icons/IcoUp'
import IcoTrash from '../icons/IcoTrash'
import BrandMark from './BrandMark'
import { BtnDanger, BtnGhost, BtnPrimary } from '../ui/buttons'
import { Field, FieldError, Input, Label, Row2 } from '../ui/fields'
import {
  cadastraCartao,
  cadastraCartaoVigente,
  deletaCartao,
  listaCartoes
} from '../../../../api/cartaoCredito.servico'
import { adyenValidaCartao } from '../../../../api/adyen.servico'
import { RECAPTCHA_KEY, resetCaptcha } from '../../../../utils/recaptcha'
import { theme } from '../../styles'
import {
  CardPreview,
  CardBrand,
  CardChip,
  CardNumber,
  CardRow,
  CardCol,
  CardKey,
  CardValue
} from './card-form-styles'
import {
  Callout,
  SuccessBox,
  SuccessSeal
} from './cancel-flow-styles'
import {
  Listnote,
  ItemList,
  Itemrow,
  ItemrowGrip,
  ItemrowBody,
  ItemrowTags,
  ItemrowActs,
  IconBtn,
  Tag,
  EmptyList,
  Hint,
  MenuActions,
  OnlyDesktop,
  OnlyMobile
} from './payment-flow-styles'

const MSG_ERRO_GENERICO = 'Não foi possível concluir a operação. Tente novamente.'

function mapCartaoApi( api ) {
  let exp = ''
  if ( api.validade ) {
    const validade = String( api.validade )
    if ( validade.includes( '-' ) ) {
      const partes = validade.split( '-' )
      if ( partes.length >= 2 ) {
        exp = `${partes[ 1 ]}/${partes[ 0 ].slice( -2 )}`
      }
    } else {
      exp = validade
    }
  }
  return {
    'id': api.id,
    'brand': api.bandeira || '',
    'last4': api.ultimosDigitos || '',
    exp,
    'name': api.nome || ''
  }
}

function ordenarCartoes( lista, cartaoVigenteId ) {
  if ( !Array.isArray( lista ) ) {
    return []
  }
  if ( !cartaoVigenteId ) {
    return lista
  }
  const vigentes = lista.filter( ( item ) => item.id === cartaoVigenteId )
  const outros = lista.filter( ( item ) => item.id !== cartaoVigenteId )
  return [ ...vigentes, ...outros ]
}

function extractMensagemErro( erro ) {
  const data = erro?.response?.data
  if ( Array.isArray( data?.errors ) && data.errors[ 0 ]?.message ) {
    return data.errors[ 0 ].message
  }
  if ( Array.isArray( data ) && data[ 0 ]?.message ) {
    return data[ 0 ].message
  }
  if ( typeof data === 'string' && data.length > 0 && data.length < 200 ) {
    return data
  }
  return MSG_ERRO_GENERICO
}

function isErroCartaoDuplicado( erro ) {
  const mensagem = extractMensagemErro( erro ) || ''
  return mensagem.toLowerCase().includes( 'já cadastrado' )
}

const TITULO_POR_SOURCE = {
  'cvv': 'CVV inválido',
  'dt_validade': 'Validade inválida',
  'validade': 'Validade inválida',
  'numero': 'Número do cartão inválido',
  'bandeira': 'Bandeira do cartão não aceita',
  'captcha': 'Captcha inválido',
}

function converterErroEmModal( erro ) {
  // Erro do CSE local (formato: { cvv: '...', validade: '...' })
  if ( erro && typeof erro === 'object' && !erro.response ) {
    const camposSimples = [ 'cvv', 'validade', 'dt_validade', 'numero', 'nome', 'bandeira' ]
    for ( const campo of camposSimples ) {
      if ( erro[ campo ] ) {
        return {
          'title': TITULO_POR_SOURCE[ campo ] || 'Não foi possível cadastrar o cartão',
          'message': erro[ campo ],
        }
      }
    }
  }
  // Erro do backend com estrutura { errors: [{ source, message }] }
  const data = erro?.response?.data
  if ( Array.isArray( data?.errors ) && data.errors[ 0 ] ) {
    const [ primeiro ] = data.errors
    const { source } = primeiro
    return {
      'title': TITULO_POR_SOURCE[ source ] || 'Não foi possível cadastrar o cartão',
      'message': primeiro.message || MSG_ERRO_GENERICO,
    }
  }
  return {
    'title': 'Não foi possível cadastrar o cartão',
    'message': extractMensagemErro( erro ),
  }
}

function formatNum( value ) {
  return value.replace( /\D/gu, '' ).slice( 0, 16 )
    .replace( /(?:.{4})/gu, ( match ) => `${match} ` )
    .trim()
}

function formatExp( value ) {
  const digits = value.replace( /\D/gu, '' ).slice( 0, 4 )
  return digits.length >= 3 ? `${digits.slice( 0, 2 )}/${digits.slice( 2 )}` : digits
}

function detectBrand( num ) {
  const digits = num.replace( /\s/gu, '' )
  return Payment.fns.cardType( digits ) || ''
}

function renderCardTag( { isValidating, isPrincipal } ) {
  if ( isValidating ) {
    return <Tag $validating>validando</Tag>
  }
  if ( isPrincipal ) {
    return <Tag>principal</Tag>
  }
  return <Tag $off>inativo</Tag>
}

function tituloBotaoPrincipal( { isValidating, isSettingPrimary } ) {
  if ( isValidating ) {
    return 'Aguarde a validação do cartão'
  }
  if ( isSettingPrimary ) {
    return 'Alterando…'
  }
  return 'Tornar principal'
}

function tituloBotaoExcluir( { isValidating, isPrincipal, alterandoPrincipal } ) {
  if ( isValidating ) {
    return 'Aguarde a validação do cartão'
  }
  if ( alterandoPrincipal ) {
    return 'Aguarde a alteração do cartão principal'
  }
  if ( isPrincipal ) {
    return 'Torne outro cartão principal antes de excluir este'
  }
  return 'Excluir'
}

export default function PaymentFlow( {
  assinaturaId,
  cardsInitial,
  cartaoVigenteId,
  onCards,
  onCartaoVigenteChange,
  'onClose': onCloseProp,
  onPay,
  pay,
  toast
} ) {
  const [ view, setView ] = useState( 'menu' )
  const [ target, setTarget ] = useState( null )
  const [ num, setNum ] = useState( '' )
  const [ name, setName ] = useState( '' )
  const [ exp, setExp ] = useState( '' )
  const [ cvv, setCvv ] = useState( '' )
  const [ err, setErr ] = useState( {} )
  const [ cards, setCards ] = useState( cardsInitial || [] )
  const [ loading, setLoading ] = useState( true )
  const [ submitting, setSubmitting ] = useState( false )
  const [ settingPrimaryId, setSettingPrimaryId ] = useState( null )
  const [ trocandoParaCartao, setTrocandoParaCartao ] = useState( false )
  const [ deleting, setDeleting ] = useState( false )
  const [ dragId, setDragId ] = useState( null )
  const [ overId, setOverId ] = useState( null )
  const [ ultimoSalvo, setUltimoSalvo ] = useState( null )
  const [ captcha, setCaptcha ] = useState( null )
  const [ validandoId, setValidandoId ] = useState( null )
  const [ pollingTimedOut, setPollingTimedOut ] = useState( false )
  const [ errorInfo, setErrorInfo ] = useState( null )
  const captchaWidgetRenderizadoRef = useRef( false )
  const pollingRef = useRef( null )
  const cancelarPollingRef = useRef( false )
  const validandoIdRef = useRef( null )

  useEffect( () => {
    validandoIdRef.current = validandoId
  }, [ validandoId ] )

  const brand = detectBrand( num ) || ''
  const pendLast4 = num.replace( /\s/gu, '' ).slice( -4 )
  const masked = num
    ? num.slice( 0, -4 ).replace( /\d/gu, '•' ) + num.slice( -4 )
    : '•••• •••• •••• ••••'

  const carregarLista = useCallback( async() => {
    try {
      const resp = await listaCartoes()
      let registros = []
      if ( Array.isArray( resp?.data ) ) {
        registros = resp.data
      } else if ( Array.isArray( resp?.data?.registros ) ) {
        ( { registros } = resp.data )
      }
      const ativos = registros.filter( ( item ) => !item.softDelete )
      const mapeados = ativos.map( mapCartaoApi )
      const ordenadaDoGet = ordenarCartoes( mapeados, cartaoVigenteId )

      setCards( ( prev ) => {
        // Merge só pra UI: preserva o cartão em validação se GET não retornou
        const idValidando = validandoIdRef.current
        if ( idValidando && !mapeados.some( ( item ) => item.id === idValidando ) ) {
          const cartaoValidando = prev.find( ( item ) => item.id === idValidando )
          if ( cartaoValidando ) {
            return ordenarCartoes( [ ...mapeados, cartaoValidando ], cartaoVigenteId )
          }
        }
        return ordenadaDoGet
      } )
      onCards( ordenadaDoGet )
      return ordenadaDoGet
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
      return null
    }
  }, [ cartaoVigenteId, onCards, toast ] )

  useEffect( () => {
    let cancelado = false
    setLoading( true )
    carregarLista()
      .finally( () => {
        if ( !cancelado ) {
          setLoading( false )
        }
      } )
    return () => {
      cancelado = true
    }
  }, [ carregarLista ] )

  useEffect( () => {
    if ( view !== 'form' ) {
      captchaWidgetRenderizadoRef.current = false
      return
    }
    if ( captchaWidgetRenderizadoRef.current ) {
      return
    }
    const { grecaptcha } = window
    if ( !grecaptcha || !grecaptcha.enterprise ) {
      return
    }
    const el = document.getElementById( 'divCaptchaBookster' )
    if ( !el || el.childElementCount > 0 ) {
      return
    }
    grecaptcha.enterprise.render( 'divCaptchaBookster', {
      'sitekey': `${RECAPTCHA_KEY}`,
      'callback': ( token ) => setCaptcha( token ),
      'expired-callback': () => setCaptcha( null ),
    } )
    captchaWidgetRenderizadoRef.current = true
  }, [ view ] )

  useEffect( () => {
    return () => {
      cancelarPollingRef.current = true
      if ( pollingRef.current ) {
        clearTimeout( pollingRef.current )
        pollingRef.current = null
      }
    }
  }, [] )

  function openNew() {
    setNum( '' )
    setName( '' )
    setExp( '' )
    setCvv( '' )
    setErr( {} )
    setView( 'form' )
  }

  function validate() {
    const next = {}
    if ( num.replace( /\s/gu, '' ).length < 15 ) {
      next.num = 'Número incompleto'
    }
    if ( name.trim().length < 3 ) {
      next.name = 'Informe o nome impresso no cartão'
    }
    if ( !( /^\d{2}\/\d{2}$/u ).test( exp ) ) {
      next.exp = 'Use MM/AA'
    }
    if ( cvv.length < 3 ) {
      next.cvv = '3 ou 4 dígitos'
    }
    setErr( next )
    return Object.keys( next ).length === 0
  }

  function cancelarPolling() {
    cancelarPollingRef.current = true
    if ( pollingRef.current ) {
      clearTimeout( pollingRef.current )
      pollingRef.current = null
    }
  }

  function onClose() {
    cancelarPolling()
    onCloseProp()
  }

  function iniciarPollingValidacao( cartaoId, deveTrocarFormaPagamento ) {
    const MAX_TENTATIVAS = 20
    const INTERVALO_MS = 3000
    let tentativas = 0
    cancelarPollingRef.current = false
    setPollingTimedOut( false )

    const checar = async() => {
      if ( cancelarPollingRef.current ) {
        return
      }
      tentativas += 1
      const lista = await carregarLista()
      if ( cancelarPollingRef.current ) {
        return
      }
      const encontrado = Array.isArray( lista )
        && lista.some( ( item ) => item.id === cartaoId )
      if ( encontrado ) {
        setValidandoId( null )
        if ( deveTrocarFormaPagamento && assinaturaId ) {
          try {
            await cadastraCartaoVigente( {
              'assinatura_id': assinaturaId,
              'cartao_credito': { 'id': cartaoId }
            } )
            const cartaoTornadoVigente = lista.find( ( item ) => item.id === cartaoId )
            if ( cartaoTornadoVigente ) {
              onCartaoVigenteChange( cartaoTornadoVigente )
            }
            await carregarLista()
            onPay( 'card' )
          } catch ( _erro ) {
            // Se ainda der 'cartão em processamento', o user pode tornar principal manualmente depois
          }
        }
        return
      }
      if ( tentativas < MAX_TENTATIVAS ) {
        pollingRef.current = setTimeout( checar, INTERVALO_MS )
      } else {
        setPollingTimedOut( true )
      }
    }

    pollingRef.current = setTimeout( checar, INTERVALO_MS )
  }

  async function applySave() {
    if ( submitting ) {
      return
    }
    if ( !validate() ) {
      return
    }
    setSubmitting( true )
    setErr( {} )
    try {
      const dtValidade = exp.replace( '/', '' )
      const cartaoValidado = await adyenValidaCartao( {
        cvv,
        'dt_validade': dtValidade,
        'nome': name,
        'numero': num.replace( /\s/gu, '' )
      } )

      const respostaPost = await cadastraCartao( {
        captcha,
        'cartao_credito': cartaoValidado,
        'cobrar': false
      } )
      const cartaoCriadoApi = respostaPost?.data?.cartaoCredito
      if ( !cartaoCriadoApi?.id ) {
        throw new Error( 'Resposta de cadastro sem cartão.' )
      }
      const cartaoCriadoUI = mapCartaoApi( cartaoCriadoApi )
      const veioDoFluxoPix = pay === 'pix'

      setCards( ( prev ) => {
        const semDuplicata = prev.filter( ( item ) => item.id !== cartaoCriadoUI.id )
        return [ ...semDuplicata, cartaoCriadoUI ]
      } )
      onCards( [ ...cards.filter( ( item ) => item.id !== cartaoCriadoUI.id ), cartaoCriadoUI ] )
      setValidandoId( cartaoCriadoUI.id )
      setUltimoSalvo( { brand, 'last4': pendLast4, exp, name, 'veioDePixCartao': veioDoFluxoPix } )
      setView( 'saved' )
      iniciarPollingValidacao( cartaoCriadoUI.id, veioDoFluxoPix )
    } catch ( erro ) {
      try {
        resetCaptcha()
      } catch ( _err ) {
        // widget pode não estar renderizado; ignorar
      }
      setCaptcha( null )
      if ( isErroCartaoDuplicado( erro ) ) {
        setView( 'duplicado' )
      } else {
        setErrorInfo( converterErroEmModal( erro ) )
        setView( 'erro' )
      }
    } finally {
      setSubmitting( false )
    }
  }

  async function makeCardPrimary( card ) {
    if ( settingPrimaryId || !assinaturaId ) {
      return
    }
    setSettingPrimaryId( card.id )
    try {
      await cadastraCartaoVigente( {
        'assinatura_id': assinaturaId,
        'cartao_credito': { 'id': card.id }
      } )
      onCartaoVigenteChange( card )
      await carregarLista()
      if ( pay === 'pix' ) {
        onPay( 'card' )
        toast( 'Forma de pagamento alterada para cartão de crédito.' )
      } else {
        toast( 'Cartão principal alterado.' )
      }
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
    } finally {
      setSettingPrimaryId( null )
    }
  }

  async function alterarParaCartao() {
    if ( trocandoParaCartao || settingPrimaryId ) {
      return
    }
    setTrocandoParaCartao( true )
    try {
      const resposta = await carregarLista()
      const lista = Array.isArray( resposta ) ? resposta : []
      const cartaoEscolhido = lista.find( ( item ) => item.id !== cartaoVigenteId ) || lista[ 0 ]
      if ( !cartaoEscolhido || !assinaturaId ) {
        openNew()
        return
      }
      await makeCardPrimary( cartaoEscolhido )
    } finally {
      setTrocandoParaCartao( false )
    }
  }

  function handleCardDrop( destino ) {
    const origemId = dragId
    setDragId( null )
    setOverId( null )
    if ( !origemId || origemId === destino.id || settingPrimaryId || validandoId ) {
      return
    }
    const origemIndex = cards.findIndex( ( item ) => item.id === origemId )
    const destinoIndex = cards.findIndex( ( item ) => item.id === destino.id )
    if ( origemIndex < 0 || destinoIndex < 0 ) {
      return
    }
    const reordenada = [ ...cards ]
    const [ movido ] = reordenada.splice( origemIndex, 1 )
    reordenada.splice( destinoIndex, 0, movido )
    setCards( reordenada )
    if ( reordenada[ 0 ].id !== cards[ 0 ].id ) {
      makeCardPrimary( reordenada[ 0 ] )
    }
  }

  async function handleDelete() {
    if ( deleting || !target ) {
      return
    }
    setDeleting( true )
    try {
      await deletaCartao( { 'id': target.id, 'idAssinatura': assinaturaId } )
      await carregarLista()
      setView( 'deleted' )
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
      setView( 'menu' )
    } finally {
      setDeleting( false )
    }
  }

  if ( view === 'saved' ) {
    const salvo = ultimoSalvo || { brand, 'last4': pendLast4 }
    const textoValidacao = salvo.veioDePixCartao
      ? 'Sua forma de pagamento será alterada para Cartão de crédito assim que a validação for concluída.'
      : 'Após a validação, ele fica disponível para ser marcado como principal.'
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ () => setView( 'menu' ) }
            type="button"
          >
            Voltar aos cartões
          </BtnPrimary>
        ) }
        onClose={ onClose }
        title="Cartão cadastrado"
      >
        <SuccessBox>
          <SuccessSeal $orange>
            <IcoCard
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Cartão cadastrado</h2>
          <p>
            O cartão <b>{ salvo.brand } •••• { salvo.last4 }</b> está sendo
            validado pela operadora — isso pode levar alguns instantes.
            { ' ' }{ textoValidacao }
          </p>
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'erro' && errorInfo ) {
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ () => setView( 'form' ) }
            type="button"
          >
            Voltar
          </BtnPrimary>
        ) }
        onClose={ onClose }
        title={ errorInfo.title }
      >
        <SuccessBox>
          <SuccessSeal $stop>
            <IcoCard
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">{ errorInfo.title }</h2>
          <p>{ errorInfo.message }</p>
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'duplicado' ) {
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ () => setView( 'menu' ) }
            type="button"
          >
            Voltar aos cartões
          </BtnPrimary>
        ) }
        onClose={ onClose }
        title="Cartão já cadastrado"
      >
        <SuccessBox>
          <SuccessSeal $stop>
            <IcoCard
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Este cartão já está na sua lista</h2>
          <p>
            Encontramos um cartão com esses mesmos dados no seu cadastro.
            Se quiser utilizá-lo, procure-o na lista de cartões cadastrados.
          </p>
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'deleted' ) {
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ () => setView( 'menu' ) }
            type="button"
          >
            Voltar aos cartões
          </BtnPrimary>
        ) }
        onClose={ onClose }
        title="Cartão excluído"
      >
        <SuccessBox>
          <SuccessSeal $stop>
            <IcoTrash
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Cartão removido</h2>
          <p>
            Removemos o cartão da sua lista. Mantenha ao menos um cartão salvo
            para as próximas renovações.
          </p>
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'del' && target ) {
    return (
      <Modal
        canClose={ !deleting }
        eyebrow="Pagamento"
        footer={ (
          <>
            <BtnGhost
              disabled={ deleting }
              onClick={ () => setView( 'menu' ) }
              type="button"
            >Voltar
            </BtnGhost>
            <BtnDanger
              disabled={ deleting }
              onClick={ handleDelete }
              type="button"
            >{ deleting ? 'Excluindo…' : 'Excluir cartão' }
            </BtnDanger>
          </>
        ) }
        onClose={ onClose }
        title="Excluir cartão?"
      >
        <Callout>
          <IcoTrash
            height="20"
            style={{ 'color': theme.cores.statusStop, 'flex': 'none' }}
            width="20"
          />
          <span>
            Vamos remover o cartão <b>{ target.brand } •••• { target.last4 }</b>.
            Você pode cadastrar outro quando quiser.
          </span>
        </Callout>
      </Modal>
    )
  }

  if ( view === 'form' ) {
    return (
      <Modal
        canClose={ !submitting }
        eyebrow="Pagamento"
        footer={ (
          <>
            <BtnGhost
              disabled={ submitting }
              onClick={ () => setView( 'menu' ) }
              type="button"
            >Voltar
            </BtnGhost>
            <BtnPrimary
              disabled={ submitting || !captcha }
              onClick={ applySave }
              type="button"
            >
              { submitting ? 'Salvando…' : 'Cadastrar cartão' }
            </BtnPrimary>
          </>
        ) }
        onClose={ onClose }
        sub="Os dados são usados apenas para a cobrança recorrente da sua assinatura."
        title="Cadastrar novo cartão"
      >
        <CardPreview>
          <CardBrand>{ brand || '—' }</CardBrand>
          <CardChip />
          <CardNumber>{ masked }</CardNumber>
          <CardRow>
            <CardCol>
              <CardKey>Titular</CardKey>
              <CardValue>{ name || 'NOME NO CARTÃO' }</CardValue>
            </CardCol>
            <CardCol>
              <CardKey>Validade</CardKey>
              <CardValue>{ exp || 'MM/AA' }</CardValue>
            </CardCol>
          </CardRow>
        </CardPreview>

        <Field>
          <Label>Número do cartão</Label>
          <Input
            $err={ Boolean( err.num ) }
            disabled={ submitting }
            inputMode="numeric"
            onChange={ ( event ) => setNum( formatNum( event.target.value ) ) }
            placeholder="0000 0000 0000 0000"
            value={ num }
          />
          { err.num ? <FieldError>{ err.num }</FieldError> : null }
        </Field>
        <Field>
          <Label>Nome impresso no cartão</Label>
          <Input
            $err={ Boolean( err.name ) }
            disabled={ submitting }
            onChange={ ( event ) => setName( event.target.value.toUpperCase() ) }
            placeholder="Como aparece no cartão"
            value={ name }
          />
          { err.name ? <FieldError>{ err.name }</FieldError> : null }
        </Field>
        <Row2>
          <Field>
            <Label>Validade</Label>
            <Input
              $err={ Boolean( err.exp ) }
              disabled={ submitting }
              inputMode="numeric"
              onChange={ ( event ) => setExp( formatExp( event.target.value ) ) }
              placeholder="MM/AA"
              value={ exp }
            />
            { err.exp ? <FieldError>{ err.exp }</FieldError> : null }
          </Field>
          <Field>
            <Label>CVV</Label>
            <Input
              $err={ Boolean( err.cvv ) }
              disabled={ submitting }
              inputMode="numeric"
              maxLength={ 4 }
              onChange={ ( event ) => setCvv( event.target.value.replace( /\D/gu, '' ).slice( 0, 4 ) ) }
              placeholder="000"
              value={ cvv }
            />
            { err.cvv ? <FieldError>{ err.cvv }</FieldError> : null }
          </Field>
        </Row2>
        <div
          data-action="CHECKOUT"
          data-sitekey={ RECAPTCHA_KEY }
          id="divCaptchaBookster"
          style={{ 'marginTop': 16 }}
        />
      </Modal>
    )
  }

  // view === 'menu'
  const podeExcluir = cards.length > 1
  const alterandoPrincipal = Boolean( settingPrimaryId )
  return (
    <Modal
      canClose={ !alterandoPrincipal }
      eyebrow="Pagamento"
      footer={ (
        <BtnGhost
          disabled={ alterandoPrincipal }
          onClick={ onClose }
          type="button"
        >{ alterandoPrincipal ? 'Aguarde…' : 'Fechar' }
        </BtnGhost>
      ) }
      onClose={ onClose }
      sub="Gerencie a forma de pagamento da sua assinatura."
      title="Formas de pagamento"
    >
      { pay === 'pix' && validandoId ? (
        <Callout>
          <IcoCard
            height="20"
            style={{ 'color': theme.cores.accent, 'flex': 'none' }}
            width="20"
          />
          <span>
            { pollingTimedOut ? (
              <>
                A validação do cartão está demorando mais que o esperado.
                Você pode aguardar e atualizar a página em alguns minutos, ou
                tentar cadastrar outro cartão.
              </>
            ) : (
              <>
                Aguardando validação do cartão pela operadora. Sua forma de
                pagamento será alterada para <b>Cartão de crédito</b> assim que
                a validação for concluída.
              </>
            ) }
          </span>
        </Callout>
      ) : null }

      { pay === 'card' ? (
        <>
          { loading ? (
            <EmptyList>Carregando seus cartões…</EmptyList>
          ) : null }
          { !loading && cards.length === 0 ? (
            <EmptyList>
              Nenhum cartão cadastrado. Cadastre um para manter a assinatura ativa.
            </EmptyList>
          ) : null }
          { !loading && cards.length > 1 ? (
            <Listnote>
              <IcoCard
                height="15"
                width="15"
              />
              <span>
                O cartão <b>principal</b> é o usado na cobrança.
                { ' ' }
                <OnlyDesktop><b>Arraste um cartão para o topo</b> para torná-lo principal.</OnlyDesktop>
                <OnlyMobile>Toque na <b>seta ↑</b> de um cartão para torná-lo principal.</OnlyMobile>
              </span>
            </Listnote>
          ) : null }
          { !loading && cards.length > 0 ? (
            <ItemList>
              { cards.map( ( card, indice ) => {
                const isValidating = card.id === validandoId
                const isPrincipal = !isValidating && indice === 0
                const isSettingPrimary = settingPrimaryId === card.id
                const bloqueiaExcluir = isPrincipal || isValidating || alterandoPrincipal
                const bloqueiaPrincipal = isSettingPrimary || Boolean( settingPrimaryId ) || isValidating
                return (
                  <Itemrow
                    $drag={ !isValidating }
                    $dragging={ dragId === card.id }
                    $over={ overId === card.id && dragId !== card.id }
                    $primary={ isPrincipal }
                    $validating={ isValidating }
                    draggable={ !isValidating }
                    key={ card.id }
                    onDragEnd={ () => {
                      setDragId( null )
                      setOverId( null )
                    } }
                    onDragLeave={ () => {
                      if ( overId === card.id ) {
                        setOverId( null )
                      }
                    } }
                    onDragOver={ ( event ) => {
                      event.preventDefault()
                      if ( overId !== card.id ) {
                        setOverId( card.id )
                      }
                    } }
                    onDragStart={ ( event ) => {
                      setDragId( card.id )
                      event.dataTransfer.effectAllowed = 'move'
                      event.dataTransfer.setData( 'text/plain', card.id )
                    } }
                    onDrop={ ( event ) => {
                      event.preventDefault()
                      handleCardDrop( card )
                    } }
                  >
                    <ItemrowGrip
                      aria-hidden="true"
                      title="Arraste para reordenar"
                    >
                      <IcoGrip
                        height="16"
                        width="16"
                      />
                    </ItemrowGrip>
                    <ItemrowBody>
                      <ItemrowTags>
                        <BrandMark brand={ card.brand } />
                        { renderCardTag( { isValidating, isPrincipal } ) }
                      </ItemrowTags>
                      <b>•••• { card.last4 }</b>
                      { card.exp ? <span>Validade { card.exp }</span> : null }
                    </ItemrowBody>
                    <ItemrowActs>
                      { !isPrincipal ? (
                        <IconBtn
                          aria-label="Tornar principal"
                          disabled={ bloqueiaPrincipal }
                          onClick={ () => makeCardPrimary( card ) }
                          title={ tituloBotaoPrincipal( { isValidating, isSettingPrimary } ) }
                          type="button"
                        >
                          <IcoUp
                            height="16"
                            width="16"
                          />
                        </IconBtn>
                      ) : null }
                      { podeExcluir ? (
                        <IconBtn
                          $danger
                          aria-label="Excluir cartão"
                          disabled={ bloqueiaExcluir }
                          onClick={ () => {
                            setTarget( card )
                            setView( 'del' )
                          } }
                          title={ tituloBotaoExcluir( { isValidating, isPrincipal, alterandoPrincipal } ) }
                          type="button"
                        >
                          <IcoTrash
                            height="16"
                            width="16"
                          />
                        </IconBtn>
                      ) : null }
                    </ItemrowActs>
                  </Itemrow>
                )
              } ) }
            </ItemList>
          ) : null }
          { !loading ? (
            <MenuActions>
              <BtnGhost
                disabled={ alterandoPrincipal }
                onClick={ openNew }
                title={ alterandoPrincipal ? 'Aguarde a alteração do cartão principal' : null }
                type="button"
              >
                { cards.length === 0 ? 'Cadastrar cartão' : 'Cadastrar novo cartão' }
              </BtnGhost>
            </MenuActions>
          ) : null }
        </>
      ) : (
        <>
          <Hint>
            Quando chegar a data da sua renovação automática, enviaremos um <b>QR Code por e-mail</b> para você realizar o pagamento.
          </Hint>
          <Hint>
            Se preferir pagar com cartão de crédito, clique em <b>Alterar forma de pagamento</b>.
          </Hint>
          <MenuActions>
            <BtnGhost
              disabled={ trocandoParaCartao || ( Boolean( validandoId ) && !pollingTimedOut ) }
              onClick={ alterarParaCartao }
              title={ validandoId && !pollingTimedOut
                ? 'Aguardando validação do cartão em andamento'
                : 'Alterar forma de pagamento' }
              type="button"
            >{ trocandoParaCartao ? 'Alterando…' : 'Alterar forma de pagamento' }
            </BtnGhost>
          </MenuActions>
        </>
      ) }
    </Modal>
  )
}

PaymentFlow.propTypes = {
  'assinaturaId': number,
  'cardsInitial': arrayOf( object ),
  'cartaoVigenteId': number,
  'onCards': func.isRequired,
  'onCartaoVigenteChange': func,
  'onClose': func.isRequired,
  'onPay': func.isRequired,
  'pay': string.isRequired,
  'toast': func.isRequired
}

PaymentFlow.defaultProps = {
  'assinaturaId': null,
  'cardsInitial': [],
  'cartaoVigenteId': null,
  'onCartaoVigenteChange': () => {}
}
